import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, Clock, Calendar, User, Sparkles } from 'lucide-react';
import SEO from '@/components/SEO';
import RenderBlogContent from '@/components/blog/RenderBlogContent';
import {
  getPostBySlug,
  getRelatedPosts,
  getCategoryByKey,
} from '@/data/blog/posts';

const formatDate = (iso) => {
  // Parse YYYY-MM-DD as a local date to avoid UTC→local day-shift.
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;
  if (post.comingSoon) return <Navigate to="/blog" replace />;

  const category = getCategoryByKey(post.category);
  const related = getRelatedPosts(post.slug, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    image: post.coverImage || post.thumbnail,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: {
      '@type': 'Organization',
      name: post.author || 'Navs Art Studio',
      url: 'https://www.navsartstudio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Navs Art Studio',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.navsartstudio.com/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.navsartstudio.com/blog/${post.slug}`,
    },
    articleSection: category?.label,
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: 'linear-gradient(180deg, #FAF7F1 0%, #F4F1EA 35%, #ECF0F0 100%)' }}
      data-testid="blog-post-page"
    >
      <SEO
        title={post.title}
        metaTitle={post.metaTitle}
        description={post.metaDescription || post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.coverImage || post.thumbnail}
        jsonLd={jsonLd}
      />

      {/* ── Hero banner ── */}
      <section className="relative w-full h-[68vh] min-h-[480px] max-h-[760px] overflow-hidden">
        <img
          src={post.coverImage || post.thumbnail}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: post.coverPosition || 'center' }}
          loading="eager"
        />
        {/* Ocean cinematic wash */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(11,31,38,0.55) 0%, rgba(11,31,38,0.35) 45%, rgba(11,31,38,0.85) 100%)',
          }}
        />
        {/* Subtle gold sheen */}
        <div
          aria-hidden
          className="absolute -top-1/4 -right-1/4 w-[60%] h-[160%] opacity-[0.08] pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, #D4AF5A 0%, transparent 60%)',
          }}
        />

        <div className="relative z-10 h-full flex items-end">
          <div className="max-w-4xl mx-auto px-6 md:px-10 pb-16 sm:pb-20 lg:pb-24 w-full">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-white/85 hover:text-[#D4AF5A] transition-colors mb-6"
                data-testid="blog-post-back"
              >
                <ArrowLeft size={12} />
                The Navs Journal
              </Link>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-5 text-[11px] uppercase tracking-[0.22em]" style={{ color: '#E8D9B5' }}>
                <Sparkles size={11} className="text-[#D4AF5A]" />
                <span>{category?.label}</span>
                {post.secondaryCategory && (
                  <>
                    <span className="text-[#D4AF5A]">•</span>
                    <span>{post.secondaryCategory}</span>
                  </>
                )}
              </div>
              <h1
                className="text-white text-3xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-[-0.012em] mb-6"
                style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
                data-testid="blog-post-title"
              >
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-white/85 text-xs sm:text-sm">
                <span className="flex items-center gap-1.5">
                  <User size={13} className="text-[#D4AF5A]" />
                  Published by {post.author || 'Navs Art Studio'}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={13} className="text-[#D4AF5A]" />
                  {formatDate(post.publishedAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock size={13} className="text-[#D4AF5A]" />
                  {post.readingTime}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Article body ── */}
      <article className="max-w-3xl mx-auto px-6 md:px-10 pt-20 sm:pt-24 pb-16 sm:pb-20">
        <div
          className="h-[1px] w-16 mb-12 sm:mb-14"
          style={{ background: 'linear-gradient(90deg, #D4AF5A 0%, transparent 100%)' }}
        />
        <RenderBlogContent blocks={post.content} />

        {/* ── Inline collection link ── */}
        <div className="mt-20 sm:mt-24 p-7 sm:p-8 rounded-2xl bg-white/70 backdrop-blur-sm border border-[#0B1F26]/10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#1E4A55] mb-2">
            Discover more
          </p>
          <p className="text-[#0B1F26] text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Explore our ocean-inspired collection or commission a piece designed entirely around your story.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/ocean"
              className="text-xs uppercase tracking-[0.22em] text-[#0B1F26] hover:text-[#D4AF5A] transition-colors flex items-center gap-1.5"
            >
              Ocean Series <ArrowRight size={12} />
            </Link>
            <span className="text-[#D4AF5A]">•</span>
            <Link
              to="/featured"
              className="text-xs uppercase tracking-[0.22em] text-[#0B1F26] hover:text-[#D4AF5A] transition-colors flex items-center gap-1.5"
            >
              Featured Artwork <ArrowRight size={12} />
            </Link>
            <span className="text-[#D4AF5A]">•</span>
            <Link
              to="/contact"
              className="text-xs uppercase tracking-[0.22em] text-[#0B1F26] hover:text-[#D4AF5A] transition-colors flex items-center gap-1.5"
            >
              Custom Commission <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </article>

      {/* ── Bottom CTA ── */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="rounded-3xl px-8 sm:px-14 py-14 sm:py-16 text-center relative overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, #0B1F26 0%, #143942 55%, #1E4A55 100%)',
          }}
          data-testid="blog-post-cta"
        >
          <div
            aria-hidden
            className="absolute -top-1/2 -right-1/4 w-[55%] h-[200%] opacity-[0.08] pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at center, #D4AF5A 0%, transparent 60%)',
            }}
          />
          <p
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] mb-4"
            style={{ color: '#D4AF5A' }}
          >
            One-of-a-kind, made for you
          </p>
          <h2
            className="text-white text-3xl sm:text-4xl lg:text-[44px] leading-tight mb-8"
            style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
          >
            Ready to <em className="italic" style={{ color: '#E8D9B5' }}>transform your space?</em>
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" data-testid="blog-post-cta-custom">
              <button
                className="group rounded-full px-8 py-[14px] text-xs tracking-[0.18em] uppercase font-medium text-[#0B1F26] transition-all duration-500 hover:shadow-[0_10px_30px_rgba(212,175,90,0.45)] hover:-translate-y-0.5"
                style={{
                  background:
                    'linear-gradient(135deg, #F1E0B0 0%, #D4AF5A 55%, #B8923E 100%)',
                }}
              >
                <span className="flex items-center gap-2">
                  Create Your Custom Piece
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            </Link>
            <Link to="/featured" data-testid="blog-post-cta-explore">
              <button className="group rounded-full px-8 py-[14px] text-xs tracking-[0.18em] uppercase font-medium text-white border border-white/30 backdrop-blur-md bg-white/5 hover:bg-white/15 hover:border-white/70 transition-all duration-500 hover:-translate-y-0.5">
                <span className="flex items-center gap-2">
                  Explore Collection
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </button>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ── Related posts ── */}
      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24" data-testid="blog-related">
          <div className="flex items-baseline justify-between mb-8">
            <h2
              className="text-2xl sm:text-3xl text-[#0B1F26] tracking-[-0.005em]"
              style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
            >
              Continue reading
            </h2>
            <Link
              to="/blog"
              className="text-xs uppercase tracking-[0.22em] text-[#0B1F26] hover:text-[#D4AF5A] transition-colors flex items-center gap-1.5"
            >
              All articles <ArrowRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((p) => {
              const cat = getCategoryByKey(p.category);
              const Card = (
                <article
                  className="group rounded-2xl overflow-hidden bg-white border border-[#0B1F26]/8 hover:border-[#D4AF5A]/55 shadow-[0_8px_24px_-18px_rgba(11,31,38,0.3)] hover:shadow-[0_18px_40px_-22px_rgba(11,31,38,0.4)] transition-all duration-500 h-full flex flex-col"
                  data-testid={`blog-related-${p.slug}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-[#E8EEEF]">
                    {p.thumbnail && (
                      <img
                        src={p.thumbnail}
                        alt={p.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.05]"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
                    {p.comingSoon && (
                      <span className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full bg-[#0B1F26]/85 backdrop-blur-md text-[9px] uppercase tracking-[0.22em] text-[#E8D9B5]">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#1E4A55] mb-2">
                      {cat?.label}
                    </p>
                    <h3
                      className="text-base sm:text-lg leading-snug text-[#0B1F26] mb-3 tracking-[-0.005em] flex-1"
                      style={{ fontFamily: 'Playfair Display, serif', fontWeight: 500 }}
                    >
                      {p.title}
                    </h3>
                    <div className="flex items-center justify-between pt-3 border-t border-[#0B1F26]/8 text-[10px] uppercase tracking-[0.2em] text-[#4A4A4A]">
                      <span>{p.readingTime}</span>
                      {!p.comingSoon && (
                        <span className="flex items-center gap-1 text-[#0B1F26] group-hover:text-[#D4AF5A] transition-colors">
                          Read <ArrowRight size={10} />
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              );
              return p.comingSoon ? (
                <div key={p.slug} aria-disabled className="cursor-default opacity-90">
                  {Card}
                </div>
              ) : (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="block">
                  {Card}
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
